<script setup lang="ts">
const props = defineProps<{
  vacancyUid: string
  vacancyTitle: string
  contactEmail?: string
}>()

const form = reactive({
  name: '',
  email: '',
  phone: '',
  motivation: '',
  consent: false,
  company: '', // honeypot — must stay empty for real users
})
const cvFile = ref<File | null>(null)
const submitting = ref(false)
const submitted = ref(false)
const error = ref<string | null>(null)

function onFile(e: Event) {
  const input = e.target as HTMLInputElement
  const file = input.files?.[0] ?? null
  if (file && file.size > 10 * 1024 * 1024) {
    error.value = 'Bestand is te groot (max 10 MB).'
    input.value = ''
    cvFile.value = null
    return
  }
  error.value = null
  cvFile.value = file
}

function validate(): string | null {
  if (!form.name.trim()) return 'Vul je naam in.'
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return 'Vul een geldig e-mailadres in.'
  if (!form.phone.trim()) return 'Vul je telefoonnummer in.'
  if (!form.consent) return 'Ga akkoord met de privacyverklaring.'
  return null
}

async function onSubmit() {
  const problem = validate()
  if (problem) {
    error.value = problem
    return
  }
  error.value = null
  submitting.value = true
  try {
    const fd = new FormData()
    fd.append('vacancy_uid', props.vacancyUid)
    fd.append('vacancy_title', props.vacancyTitle)
    fd.append('to', props.contactEmail || '')
    fd.append('name', form.name)
    fd.append('email', form.email)
    fd.append('phone', form.phone)
    fd.append('motivation', form.motivation)
    fd.append('consent', String(form.consent))
    fd.append('company', form.company)
    if (cvFile.value) fd.append('cv', cvFile.value)
    await $fetch('/api/vacancy-application', { method: 'POST', body: fd })
    submitted.value = true
  } catch {
    error.value = 'Er ging iets mis bij het versturen. Probeer het later opnieuw.'
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <section id="vacancy-apply" class="pa-apply-form">
    <div v-if="submitted" class="pa-apply-form__success">
      <h2 class="pa-apply-form__title">Bedankt voor je sollicitatie!</h2>
      <p class="pa-apply-form__intro">Je krijgt binnen vijf werkdagen antwoord.</p>
    </div>

    <template v-else>
      <h2 class="pa-apply-form__title">Solliciteer op deze functie</h2>
      <p class="pa-apply-form__intro">
        Velden met <span class="pa-apply-form__req">*</span> zijn verplicht. Je krijgt binnen vijf werkdagen antwoord.
      </p>

      <form @submit.prevent="onSubmit">
        <div class="pa-apply-form__grid">
          <div class="pa-apply-form__field">
            <label class="pa-apply-form__label" for="apply-name">
              Naam <span class="pa-apply-form__req">*</span>
            </label>
            <input
              id="apply-name"
              v-model="form.name"
              class="pa-apply-form__input"
              type="text"
              placeholder="Voor- en achternaam"
              autocomplete="name"
            >
          </div>

          <div class="pa-apply-form__field">
            <label class="pa-apply-form__label" for="apply-email">
              E-mailadres <span class="pa-apply-form__req">*</span>
            </label>
            <input
              id="apply-email"
              v-model="form.email"
              class="pa-apply-form__input"
              type="email"
              placeholder="naam@email.nl"
              autocomplete="email"
            >
          </div>

          <div class="pa-apply-form__field">
            <label class="pa-apply-form__label" for="apply-phone">
              Telefoonnummer <span class="pa-apply-form__req">*</span>
            </label>
            <input
              id="apply-phone"
              v-model="form.phone"
              class="pa-apply-form__input"
              type="tel"
              placeholder="06 12 34 56 78"
              autocomplete="tel"
            >
          </div>

          <div class="pa-apply-form__field">
            <label class="pa-apply-form__label" for="apply-cv">CV uploaden</label>
            <label class="pa-apply-form__file" for="apply-cv">
              {{ cvFile ? cvFile.name : 'Kies bestand — pdf of docx, max 10 MB' }}
              <input
                id="apply-cv"
                type="file"
                accept=".pdf,.doc,.docx"
                @change="onFile"
              >
            </label>
          </div>

          <div class="pa-apply-form__field pa-apply-form__field--full">
            <label class="pa-apply-form__label" for="apply-motivation">Motivatie</label>
            <textarea
              id="apply-motivation"
              v-model="form.motivation"
              class="pa-apply-form__textarea"
              rows="5"
              placeholder="Vertel kort waarom je bij Pro-Air wilt werken en wat je meeneemt."
            />
          </div>
        </div>

        <div aria-hidden="true" class="sr-only">
          <label>
            Bedrijf
            <input v-model="form.company" type="text" tabindex="-1" autocomplete="off">
          </label>
        </div>

        <div class="pa-apply-form__footer">
          <label class="pa-apply-form__consent">
            <input v-model="form.consent" type="checkbox">
            <span>Ja, ik ga akkoord met de <NuxtLink to="/privacyverklaring">privacyverklaring</NuxtLink></span>
          </label>
          <button class="pa-apply-form__submit" type="submit" :disabled="submitting">
            {{ submitting ? 'Versturen…' : 'Verstuur sollicitatie →' }}
          </button>
        </div>

        <p v-if="error" class="pa-apply-form__error">{{ error }}</p>
      </form>
    </template>
  </section>
</template>
